import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { env } from '../lib/env';
import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';

/**
 * i18next setup — bundled JSON catalogues for fr/en/es.
 *
 * The frontend mirrors the backend i18n key space (`apiError.*` keys
 * match what NestJS BusinessException emits via the i18n middleware),
 * so a single `t(error.key, error.params)` call resolves any backend
 * validation or business-rule message in the user's language.
 *
 * Persisted preference: read from localStorage (key `isi-gmao.locale`),
 * fallback to env default (FR), fallback to navigator language.
 */
export const SUPPORTED_LOCALES = ['fr', 'en', 'es'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
const STORAGE_KEY = 'isi-gmao.locale';

function isSupported(v: string | null | undefined): v is SupportedLocale {
  return v === 'fr' || v === 'en' || v === 'es';
}

function detectInitialLocale(): SupportedLocale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isSupported(stored)) return stored;
  } catch {
    // localStorage unavailable (private mode, etc.) — silently ignore.
  }
  if (isSupported(env.VITE_DEFAULT_LOCALE)) return env.VITE_DEFAULT_LOCALE;
  const nav = (typeof navigator !== 'undefined' ? navigator.language : '') || '';
  const short = nav.slice(0, 2);
  if (isSupported(short)) return short;
  return 'fr';
}

void i18n.use(initReactI18next).init({
  resources: {
    fr: { translation: fr },
    en: { translation: en },
    es: { translation: es },
  },
  lng: detectInitialLocale(),
  fallbackLng: 'fr',
  interpolation: { escapeValue: false },
  returnNull: false,
});

/**
 * Switch the active language and persist the choice for future visits.
 * No-ops on an unsupported value rather than throwing — caller can rely
 * on the type guard in `SUPPORTED_LOCALES`.
 */
export function setLocale(locale: SupportedLocale): void {
  void i18n.changeLanguage(locale);
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // ignore
  }
}

export default i18n;
